import { useEffect, useRef, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Input, Text } from '@artprov/design-system'

const MONTH_NAMES = ['Ene.', 'Feb.', 'Mar.', 'Abr.', 'May.', 'Jun.', 'Jul.', 'Ago.', 'Sep.', 'Oct.', 'Nov.', 'Dic.']

const toDisplayFormat = (yyyyMm) => {
  if (!yyyyMm) return ''
  const [yyyy, mm] = yyyyMm.split('-')
  return `${mm}/${yyyy}`
}

const getMaxYear = (maxDate) => (maxDate ? parseInt(maxDate.split('-')[0]) : new Date().getFullYear() - 1)

const getMinYear = (minDate) => (minDate ? parseInt(minDate.split('-')[0]) : 2000)

const getInitialViewYear = (value, maxDate) => {
  if (value) return parseInt(value.split('-')[0])
  return getMaxYear(maxDate)
}

const isMonthDisabled = (year, monthIndex, minDate, maxDate) => {
  const date = `${year}-${String(monthIndex + 1).padStart(2, '0')}`
  if (minDate && date < minDate) return true
  if (maxDate && date > maxDate) return true
  return false
}

const getMonthButtonClass = (isSelected, monthDisabled) => {
  if (isSelected) return 'bg-secondary-90 text-primary-10'
  if (monthDisabled) return 'opacity-40 cursor-not-allowed text-tertiary-80'
  return 'text-primary-10 hover:bg-primary-95 hover:text-primary-20 cursor-pointer'
}

const getYearButtonClass = (isSelected, yearDis) => {
  if (isSelected) return 'bg-secondary-90 text-primary-10'
  if (yearDis) return 'bg-tertiary-90 cursor-not-allowed text-tertiary-40'
  return 'text-primary-10 hover:bg-primary-95 hover:text-primary-20 cursor-pointer'
}

const isYearDisabled = (year, minYear, maxYear) => year < minYear || year > maxYear

/** Ícono con el estilo del DS (Material Symbols con font-variation-settings) */
const DSIcon = ({ name, className = '' }) => (
  <span
    className={`material-symbols-outlined block text-[20px] leading-none ${className}`}
    style={{ fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20" }}
  >
    {name}
  </span>
)

DSIcon.propTypes = {
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
}

/** Botón de navegación (chevron) con el estilo del DS */
const ChevronButton = ({ icon, onClick, disabled, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    className="flex h-5 w-5 items-center justify-center rounded-[2px] text-primary-20 transition-colors hover:bg-primary-95 hover:text-primary-20 disabled:opacity-30 disabled:cursor-not-allowed focus-visible:bg-primary-95 focus-visible:outline-none"
  >
    <DSIcon name={icon} />
  </button>
)

ChevronButton.propTypes = {
  icon: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
}

/**
 * Variante del MonthPicker que usa componentes del DS:
 * - `Input` de @artprov/design-system para el campo
 * - `Text` de @artprov/design-system para los nombres de mes y año
 * - Tokens de color del DS en el popup del calendario
 * - Estilo de íconos del DS (fontVariationSettings)
 *
 * Dos vistas en el popup: meses (con año clicable) → años (grilla 4×3).
 * Emite y recibe fechas en formato YYYY-MM.
 */
export const MonthPickerDS = ({
  value = '',
  onChange,
  onBlur,
  label = '',
  minDate,
  maxDate,
  disabled = false,
  placeholder = 'mm/aaaa',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  // 'months' | 'years'
  const [view, setView] = useState('months')
  const [viewYear, setViewYear] = useState(() => getInitialViewYear(value, maxDate))
  // Base del rango visible en la grilla de años (el último año mostrado)
  const [yearRangeBase, setYearRangeBase] = useState(() => getInitialViewYear(value, maxDate))
  const containerRef = useRef(null)

  const maxYear = getMaxYear(maxDate)
  const minYear = getMinYear(minDate)
  const selectedYear = value ? parseInt(value.split('-')[0]) : null
  const selectedMonthIndex = value ? parseInt(value.split('-')[1]) - 1 : null

  // Rango de 12 años: [yearRangeBase - 11 … yearRangeBase] — igual que DS AniosCalendar
  const yearRange = Array.from({ length: 12 }, (_, i) => yearRangeBase - 11 + i)

  const open = () => {
    const initial = getInitialViewYear(value, maxDate)
    setViewYear(initial)
    setYearRangeBase(initial)
    setView('months')
    setIsOpen(true)
  }

  const close = useCallback(() => {
    setIsOpen(false)
    onBlur?.()
  }, [onBlur])

  const handleToggle = () => {
    if (disabled) return
    if (isOpen) {
      close()
    } else {
      open()
    }
  }

  const handleMonthSelect = (monthIndex) => {
    const newValue = `${viewYear}-${String(monthIndex + 1).padStart(2, '0')}`
    onChange?.(newValue)
    setIsOpen(false)
  }

  const handleYearLabelClick = () => {
    setYearRangeBase(viewYear)
    setView('years')
  }

  const handleYearSelect = (year) => {
    setViewYear(year)
    setView('months')
  }

  const handleInputBlur = (e) => {
    if (containerRef.current?.contains(e.relatedTarget)) return
    close()
  }

  const handleInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      close()
      return
    }
    if (e.key !== 'Tab') e.preventDefault()
  }

  useEffect(() => {
    if (!isOpen) return
    const handleOutsideClick = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        close()
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [isOpen, close])

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Input del DS */}
      <Input
        label={label}
        value={toDisplayFormat(value)}
        placeholder={placeholder}
        readOnly
        disabled={disabled}
        rightIcon="calendar_month"
        rightFunction={handleToggle}
        onKeyDown={handleInputKeyDown}
        onBlur={handleInputBlur}
        hints={[]}
      />

      {/* Popup del calendario con tokens del DS */}
      {isOpen && (
        <div
          className="absolute z-50 left-0 right-0 mt-2 bg-primary-98 rounded-lg py-3 shadow-lg min-w-[280px]"
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* ── VISTA MESES ── */}
          {view === 'months' && (
            <>
              {/* Encabezado: año clicable (sin flechas) */}
              <div className="flex justify-center px-3 py-2 mb-2">
                <button
                  type="button"
                  onClick={handleYearLabelClick}
                  className="flex items-center gap-1 rounded-[4px] px-2 py-0.5 text-primary-10 transition-colors hover:bg-primary-95 hover:text-primary-20 focus-visible:outline-none focus-visible:bg-primary-95"
                >
                  <Text variant="body1" className="font-semibold select-none">
                    {viewYear}
                  </Text>
                </button>
              </div>

              {/* Grilla de meses con flechas laterales centradas verticalmente */}
              <div className="flex items-center">
                <div className="w-9 flex-shrink-0 flex justify-center">
                  <ChevronButton
                    icon="chevron_left"
                    onClick={() => setViewYear((y) => y - 1)}
                    disabled={viewYear <= minYear}
                    ariaLabel="Año anterior"
                  />
                </div>
                <div className="flex-1 grid grid-cols-4 justify-items-center">
                  {MONTH_NAMES.map((name, index) => {
                    const monthDisabled = isMonthDisabled(viewYear, index, minDate, maxDate)
                    const isSelected = selectedYear === viewYear && selectedMonthIndex === index
                    return (
                      <button
                        key={name}
                        type="button"
                        disabled={monthDisabled}
                        onClick={() => handleMonthSelect(index)}
                        className={`flex h-16 w-14 items-center justify-center rounded-[2px] p-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-70 ${getMonthButtonClass(isSelected, monthDisabled)}`}
                      >
                        <Text variant="body2" className="capitalize text-center">
                          {name}
                        </Text>
                      </button>
                    )
                  })}
                </div>
                <div className="w-9 flex-shrink-0 flex justify-center">
                  <ChevronButton
                    icon="chevron_right"
                    onClick={() => setViewYear((y) => y + 1)}
                    disabled={viewYear >= maxYear}
                    ariaLabel="Año siguiente"
                  />
                </div>
              </div>
            </>
          )}

          {/* ── VISTA AÑOS ── */}
          {view === 'years' && (
            <>
              {/* Encabezado: rango de años (sin flechas) */}
              <div className="flex justify-start px-3 py-2 mb-1">
                <Text variant="body1" className="font-semibold text-primary-10 select-none">
                  {yearRange[0]} – {yearRange[11]}
                </Text>
              </div>

              {/* Grilla de años con flechas laterales centradas verticalmente */}
              <div className="flex items-center">
                <div className="w-9 flex-shrink-0 flex justify-center">
                  <ChevronButton
                    icon="chevron_left"
                    onClick={() => setYearRangeBase((b) => b - 11)}
                    disabled={yearRangeBase - 11 < minYear}
                    ariaLabel="Rango anterior"
                  />
                </div>
                <div className="flex-1 grid grid-cols-4">
                  {yearRange.map((year) => {
                    const yearDis = isYearDisabled(year, minYear, maxYear)
                    const isSelected = selectedYear === year
                    return (
                      <button
                        key={year}
                        type="button"
                        disabled={yearDis}
                        onClick={() => handleYearSelect(year)}
                        className={`flex h-16 w-full items-center justify-center rounded-[2px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-70 ${getYearButtonClass(isSelected, yearDis)}`}
                      >
                        <Text variant="body2" className="text-center">
                          {year}
                        </Text>
                      </button>
                    )
                  })}
                </div>
                <div className="w-9 flex-shrink-0 flex justify-center">
                  <ChevronButton
                    icon="chevron_right"
                    onClick={() => setYearRangeBase((b) => b + 11)}
                    disabled={yearRangeBase >= maxYear}
                    ariaLabel="Rango siguiente"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

MonthPickerDS.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
}
